import { Flex, Text } from '@chakra-ui/react';
import { OutlineCard } from '@morressier/alembic';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBooks,
  faEnvelope,
  faNewspaper,
  faShieldCheck,
  faTag,
  faUsers
} from '@fortawesome/pro-regular-svg-icons';
import { Layout } from 'src/components/Layout';
import { useQuery } from '@tanstack/react-query';
import {
  fetchWorkspaceJournals,
  fetchWorkspaceMemberships,
  fetchOrgArticleTypes,
  fetchWorkspaceTags,
  fetchWorkspaceRoles
} from 'src/api/queries';
import { useWorkspace } from '../SubmissionContexts/Users/userHook';

const WorkspaceSettings = () => {
  const { orgId, workspaceId, workspaceShortname: shortname } = useWorkspace();

  const { data: journals } = useQuery({
    queryKey: ['workspace-journals', { workspaceId }],
    queryFn: () => (workspaceId ? fetchWorkspaceJournals(workspaceId) : null)
  });

  const { data: members } = useQuery({
    queryKey: ['workspace-members', { workspaceId }],
    queryFn: () =>
      workspaceId ? fetchWorkspaceMemberships({ workspaceId }) : null
  });

  const { data: roles } = useQuery({
    queryKey: ['workspace-roles', { workspaceId }],
    queryFn: () => (workspaceId ? fetchWorkspaceRoles(workspaceId) : null)
  });

  const { data: articleTypes } = useQuery({
    queryKey: ['organization-article-types', { orgId }],
    queryFn: () =>
      orgId ? fetchOrgArticleTypes(orgId) : null
  });
  const { data: manageTags } = useQuery({
    queryKey: ['workspace-tags', { workspaceId }],
    queryFn: () => (workspaceId ? fetchWorkspaceTags(workspaceId) : null)
  });
  const data = [
    {
      url: `/app/workspace/${shortname}/manage/journals`,
      heading: 'Journals',
      description: 'Manage editorial team assignment and more.',
      count: journals?.total ?? 0,
      icon: faBooks,
      linkText: 'Manage Journals'
    },
    {
      url: `/app/workspace/${shortname}/manage/user`,
      heading: 'User Management',
      description: 'Manage workspace members and user administration.',
      count: members?.total ?? 0,
      icon: faUsers,
      linkText: 'Manage Users'
    },
    {
      url: `/app/workspace/${shortname}/manage/roles`,
      heading: 'Roles',
      description:
        'Create editorial roles and configure their permissions in submission management workflows.',
      count: Object.keys(roles?.roleConfigs ?? {}).length,
      icon: faShieldCheck,
      linkText: 'Manage Roles'
    },
    {
      url: `/app/workspace/${shortname}/manage/article-types`,
      heading: 'Article Types',
      description: 'Create article types and configure them.',
      count: articleTypes?.items.length ?? 0,
      icon: faNewspaper,
      linkText: 'Manage Article Types'
    },
    {
      url: `/app/workspace/${shortname}/manage/tags`,
      heading: 'TAGS',
      description:
        'Add and manage tags to quickly identify, categorize, and track users or submissions.',
      count: manageTags?.collection.length ?? 0,
      icon: faTag,
      linkText: 'Manage Tags'
    },
    {
      url: `/app/workspace/${shortname}/manage/emails`,
      heading: 'Emails',
      description:
        'Add and manage emails template to quickly identify, categorize, and track users or submissions.',
      count: 0,
      icon: faEnvelope,
      linkText: 'Manage Emails'
    }
  ];

  return (
    <Layout width="800px">
      <Flex flexDirection={'column'} gap={6}>
        <Text textStyle="headingTitle">Manage Workspace</Text>

        <Flex flexDirection="column" gap={4} alignItems="flex-start">
          {data.map(item => (
            <OutlineCard borderless width="100%" padding={8}>
              <Flex key={item.heading} flexDirection="column" gap={2}>
                <Flex
                  alignItems="start"
                  gap={2}
                  color={'neutralSecondaryContent'}
                >
                  <FontAwesomeIcon icon={item.icon} />
                  <Text textStyle="textContext">
                    {`${item.heading.toUpperCase()} - ${item.description}`}
                  </Text>
                </Flex>
                <Text textStyle="textStatistics">{item.count}</Text>
                <Link to={item.url}>
                  <Text
                    textStyle="actionLabel"
                    color="actionSecondaryBorderCurrent"
                  >
                    {item.linkText}
                  </Text>
                </Link>
              </Flex>
            </OutlineCard>
          ))}
        </Flex>
      </Flex>
    </Layout>
  );
};

export default WorkspaceSettings;
